import type { ElementRef, OnInit } from '@angular/core';
import {
  Component,
  computed,
  effect,
  input,
  output,
  viewChildren,
} from '@angular/core';
import { OnyxBaseFormControlComponent } from '../../../../internal/components/onyx-base-form-control/onyx-base-form-control.component';
import { INTEGER_REGEXP } from '../../../../internal/constants';
import { OnyxClearButtonComponent } from '../../../buttons/onyx-clear-button/onyx-clear-button.component';
import { OnyxIconComponent } from '../../../icons/onyx-icon/onyx-icon.component';
import { OnyxInputLabelComponent } from '../../../labels/onyx-input-label/onyx-input-label.component';
import type { OnyxTooltipContext } from '../../../tooltip/interfaces';

const TIME_PATTERN = /[0-2][0-9](:[0-5][0-9]){1,2}/;
const DURATION_PATTERN = /[0-9]{1,2}(:[0-5][0-9]){1,2}/;

export interface TimepickerInputValue {
  hour?: number;
  minutes?: number;
}

@Component({
  selector: 'onyx-timepicker-input',
  imports: [
    OnyxIconComponent,
    OnyxClearButtonComponent,
    OnyxInputLabelComponent,
  ],
  templateUrl: './onyx-timepicker-input.component.html',
  styleUrl: './onyx-timepicker-input.component.scss',
})
export class OnyxTimepickerInputComponent
  extends OnyxBaseFormControlComponent
  implements OnInit
{
  public readonly label = input<string | boolean>();
  public readonly hint = input<OnyxTooltipContext>();
  public readonly variant = input.required<'time' | 'duration'>();
  public readonly size = input<32 | 40>(40);
  public readonly showClockIcon = input(true);
  public readonly value = input.required<TimepickerInputValue>();
  public readonly invalid = input<boolean>();

  public readonly valueChange = output<TimepickerInputValue>();

  protected isTime = computed(() => this.variant() === 'time');

  private inputElementRefs =
    viewChildren<ElementRef<HTMLInputElement>>('inputElement');

  public override ngOnInit(): void {
    super.ngOnInit();

    effect(() => this.setInputValues(this.value()), {
      injector: this.injector,
    });
  }

  public focus(): void {
    this.getInputs().hour.focus();
  }

  protected changeHour(event: Event): void {
    const input = event.target as HTMLInputElement;
    const data = (event as InputEvent).data!;

    if (!data && data !== '0') {
      input.value = '';
    } else if (Number(input.value) > 23) {
      input.value = data;
    }

    const value = {
      ...this.value(),
      hour: input.value ? Number(input.value) : undefined,
    };
    if (input.value && value.minutes == null) value.minutes = 0;

    this.valueChange.emit(value);

    if (value.hour != null && value.hour > (this.isTime() ? 2 : 9)) {
      this.getInputs().minutes.focus();
    }
  }

  protected changeMinutes(event: Event): void {
    const input = event.target as HTMLInputElement;
    const data = (event as InputEvent).data!;

    if (!data && data !== '0') {
      input.value = '';
    } else if (Number(input.value) > 59) {
      input.value = data;
    }

    this.valueChange.emit({
      ...this.value(),
      minutes: input.value ? Number(input.value) : undefined,
    });
  }

  protected validateInput(event: KeyboardEvent, maxValue: number): boolean {
    if (INTEGER_REGEXP.test(event.key) && Number(event.key) < maxValue) {
      return true;
    }

    event.preventDefault();
    return false;
  }

  protected clearValue(): void {
    this.valueChange.emit({});
  }

  protected pasteTime(event: ClipboardEvent): boolean {
    const value = event.clipboardData?.getData('text');
    const pattern = this.isTime() ? TIME_PATTERN : DURATION_PATTERN;
    if (!value || !pattern.test(value)) return false;

    const [hour, minutes] = value.split(':').map(Number);
    this.valueChange.emit({ hour, minutes });

    this.getInputs().minutes.focus();
    return false;
  }

  protected focusInputs(event: Event): void {
    const target = event.target as HTMLInputElement;
    const value = this.value();
    const inputs = this.getInputs();

    if (value.hour == null) {
      inputs.hour.focus();
    } else if (value.minutes == null) {
      inputs.minutes.focus();
    } else if (target.nodeName !== 'INPUT') {
      inputs.hour.focus();
    }
  }

  protected focusHoursInput(): void {
    this.getInputs().hour.focus();
  }

  private setInputValues(value: TimepickerInputValue): void {
    const inputs = this.getInputs();

    inputs.hour.value =
      value.hour != null ? String(value.hour).padStart(2, '0') : '';

    inputs.minutes.value =
      value.minutes != null ? String(value.minutes).padStart(2, '0') : '';
  }

  private getInputs(): { hour: HTMLInputElement; minutes: HTMLInputElement } {
    const hour = this.inputElementRefs()[0].nativeElement;
    const minutes = this.inputElementRefs()[1].nativeElement;
    return { hour, minutes };
  }
}
